import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import {
  auth, ensureDriverDefaults, listenToDriverProfile, listenToDriverRides, listenToPlatformConfig,
  listenToActiveDriverRide, setDriverOnline, publishDriverLocation, clearDriverLocation,
  clearRemoteOfflineCommand, bridgeNativeAuthSession, endNativeAuthSession,
} from '../services/firebase';
import { watchPosition, getCurrentPosition } from '../services/maps';
import {
  startBackgroundLocation, stopBackgroundLocation, setLocationMode, getBatteryOptimizationStatus,
} from '../utils/backgroundLocation';
import { nextStatusForIncompleteProfile } from '../utils/profileStatus';
import { ensureLocationPermission } from '../utils/locationPermission';
import { isProfileComplete } from '../utils/driverValidation';

const DriverContext = createContext(null);
const AWAITING_PROFILE_KEY = 'mytownly_driver_awaiting_profile';

export const markAwaitingProfile = (uid) => {
  try { sessionStorage.setItem(AWAITING_PROFILE_KEY, uid); } catch { /* */ }
};
export const clearAwaitingProfile = () => {
  try { sessionStorage.removeItem(AWAITING_PROFILE_KEY); } catch { /* */ }
};

const hasStoredPhoto = (url) => {
  const s = String(url || '');
  return s.startsWith('http') || s.startsWith('data:');
};

const DRIVER_PROFILE_CACHE_KEY = 'mt_driver_profile_v1';

// A returning captain whose cached profile was authoritatively confirmed
// (approved, not blocked) within this window is cleared for protected actions
// immediately on cold start, while the server re-check runs in the background.
// Anything older, not approved, or blocked waits for the server exactly as
// before. Ride accept/start/update stay server-validated regardless, and the
// background re-check corrects the screen (pending / blocked) within seconds if
// the server disagrees. Tune from the "forced offline after optimistic unlock"
// metric.
const OPTIMISTIC_READY_MAX_AGE_MS = 48 * 60 * 60 * 1000;

const readCacheRecord = () => {
  try {
    const raw = localStorage.getItem(DRIVER_PROFILE_CACHE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

export const getCachedDriverProfile = (uid) => {
  try {
    const parsed = readCacheRecord();
    if (!parsed) return null;
    // v2 records why this copy is safe for presentation. Continue reading the
    // old raw-profile shape so existing captains get the faster startup on the
    // first upgraded launch too; actions remain locked until server validation.
    const profile = parsed?.version === 2 ? parsed.profile : parsed;
    if (!profile?.id || !isProfileComplete(profile)) return null;
    if (uid && profile.id !== uid) return null;
    return profile;
  } catch {
    return null;
  }
};

// Full v2 record + the timestamp of the last *authoritative* confirmation.
// Only the timestamped v2 shape qualifies — pre-upgrade caches return null here
// and take the normal server-wait path once, which then writes verifiedAt.
export const getCachedDriverRecord = (uid) => {
  const parsed = readCacheRecord();
  if (parsed?.version !== 2) return null;
  const profile = parsed.profile;
  if (!profile?.id || !isProfileComplete(profile)) return null;
  if (uid && profile.id !== uid) return null;
  return { profile, cachedAt: Number(parsed.cachedAt) || 0, verifiedAt: Number(parsed.verifiedAt) || 0 };
};

// Safe to unlock protected actions from this cached record before the server
// answers? Only for an approved, non-blocked captain confirmed very recently.
export const canUnlockFromCache = (record) => Boolean(
  record
  && record.profile?.isApproved === true
  && record.profile?.isBlocked !== true
  && record.verifiedAt > 0
  && (Date.now() - record.verifiedAt) < OPTIMISTIC_READY_MAX_AGE_MS,
);

export const setCachedDriverProfile = (profile, { verified = false } = {}) => {
  try {
    // Incomplete/null snapshots are never startup credentials. Only a profile
    // that has already passed the returning-driver invariant is retained, and
    // even this cache is presentation-only until live verification completes.
    if (profile?.id && isProfileComplete(profile)) {
      const prev = readCacheRecord();
      const prevVerifiedAt = (prev?.version === 2 && prev.profile?.id === profile.id)
        ? Number(prev.verifiedAt) || 0
        : 0;
      localStorage.setItem(DRIVER_PROFILE_CACHE_KEY, JSON.stringify({
        version: 2,
        uid: profile.id,
        verifiedComplete: true,
        cachedAt: Date.now(),
        // Only bumped when the server authoritatively confirmed this profile —
        // this is what the optimistic cold-start unlock keys off, never cachedAt
        // (which a non-authoritative cache echo can also refresh).
        verifiedAt: verified ? Date.now() : prevVerifiedAt,
        profile,
      }));
    }
  } catch { /* */ }
};

export const clearCachedDriverProfile = () => {
  try { localStorage.removeItem(DRIVER_PROFILE_CACHE_KEY); } catch { /* */ }
};

/** 
 * Returns true if driver is already registered (approved, or has name/vehicle number submitted).
 * Returning drivers are routed to Dashboard (which shows live map if approved, or 'Account under review' if pending).
 * Only brand-new drivers with no name/vehicle are routed to the profile setup registration form.
 */
export const DriverProvider = ({ children }) => {
  const [user, setUser] = useState(() => auth.currentUser);
  const [driver, setDriver] = useState(() => {
    const initialUser = auth.currentUser;
    return getCachedDriverProfile(initialUser?.uid);
  });
  // Firebase may need several seconds to restore WebView auth. During that
  // window the verified local profile can paint the dashboard, but no protected
  // action is enabled until profileStatus reaches `ready` from the server.
  const [authLoading, setAuthLoading] = useState(true);
  const [profileReady, setProfileReady] = useState(false);
  const [profileStatus, setProfileStatus] = useState('restoring-auth');
  // True only once the driver doc has arrived (or loaded from verified local cache).
  const [profileLoaded, setProfileLoaded] = useState(() => {
    const initialUser = auth.currentUser;
    const cached = getCachedDriverProfile(initialUser?.uid);
    return Boolean(cached && isProfileComplete(cached));
  });
  // True only after the authoritative discovery/relink workflow reaches a
  // terminal result. A cached profile alone must never mark this true.
  const [defaultsReady, setDefaultsReady] = useState(false);
  const [rides, setRides] = useState([]);
  const [activeRideDirect, setActiveRideDirect] = useState(null);
  const [releaseNotice, setReleaseNotice] = useState(null);
  const [rideOfferOpen, setRideOfferOpen] = useState(false);
  const [myPos, setMyPos] = useState(null);
  const watchIdRef = useRef(null);
  const myPosStateRef = useRef(null);
  const myPosStateAtRef = useRef(0);
  const driverOnlineRef = useRef(false);
  const activeUidRef = useRef(auth.currentUser?.uid || null);
  const driverRef = useRef(driver);
  const profileStatusRef = useRef(profileStatus);
  const resolvePromiseRef = useRef(null);
  const retryTimerRef = useRef(null);
  const driverActionsReady = Boolean(user?.uid && profileStatus === 'ready');

  useEffect(() => { driverRef.current = driver; }, [driver]);
  useEffect(() => { profileStatusRef.current = profileStatus; }, [profileStatus]);

  const resolveAuthoritativeProfile = useCallback(async (firebaseUser, { force = false } = {}) => {
    if (!firebaseUser?.uid) return null;
    if (!force && resolvePromiseRef.current?.uid === firebaseUser.uid) {
      return resolvePromiseRef.current.promise;
    }

    const promise = ensureDriverDefaults(
      firebaseUser.uid,
      firebaseUser.phoneNumber,
      { authoritative: true },
    ).then((profile) => {
      if (activeUidRef.current !== firebaseUser.uid) return profile;
      setDriver(profile);
      setProfileLoaded(true);
      setDefaultsReady(true);
      if (isProfileComplete(profile)) {
        setCachedDriverProfile(profile, { verified: true });
        setProfileStatus('ready');
      } else {
        // The authoritative read has no registration fields. Only route to
        // registration if there is NO verified-complete profile for this uid
        // anywhere — otherwise this is a transient / relink race and sending a
        // real Anna to the sign-up form is the "form flashes on every cold
        // open" bug. The live listener re-confirms `ready` either way.
        const hasVerifiedProfile = isProfileComplete(driverRef.current)
          || isProfileComplete(getCachedDriverProfile(firebaseUser.uid));
        const next = nextStatusForIncompleteProfile(profileStatusRef.current, hasVerifiedProfile);
        if (next !== profileStatusRef.current) setProfileStatus(next);
      }
      return profile;
    }).catch((error) => {
      if (activeUidRef.current === firebaseUser.uid) {
        // A transient failure (network / App Check / cold callable) must not
        // pull the rug from a captain already cleared for actions — whether
        // cleared optimistically from a fresh cache this launch, or confirmed on
        // an earlier run. Hold 'ready' and let the retry / live listener
        // re-verify. Only a captain still waiting falls back to the cached view.
        if (profileStatusRef.current !== 'ready') {
          const cached = getCachedDriverProfile(firebaseUser.uid);
          if (isProfileComplete(cached)) {
            setDriver(cached);
            setProfileLoaded(true);
            setProfileStatus('offline-cached');
          } else {
            setProfileLoaded(false);
            setProfileStatus('profile-error');
          }
          setDefaultsReady(false);
        }
      }
      throw error;
    }).finally(() => {
      if (resolvePromiseRef.current?.promise === promise) {
        resolvePromiseRef.current = null;
      }
    });

    resolvePromiseRef.current = { uid: firebaseUser.uid, promise };
    return promise;
  }, []);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser);
      setAuthLoading(false);
      activeUidRef.current = firebaseUser?.uid || null;
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
      if (!firebaseUser) {
        setDriver(null);
        setProfileReady(true);
        setProfileLoaded(false);
        setDefaultsReady(true);
        setProfileStatus('signed-out');
        clearAwaitingProfile();
        clearCachedDriverProfile();
        return;
      }
      setProfileReady(true);
      setDefaultsReady(false);
      setProfileStatus('refreshing-profile');

      const cached = getCachedDriverProfile(firebaseUser.uid);
      if (cached && isProfileComplete(cached)) {
        setDriver(cached);
        setProfileLoaded(true);
      } else {
        setDriver(null);
        setProfileLoaded(false);
      }

      // Fast path: a captain the server confirmed approved & not blocked within
      // OPTIMISTIC_READY_MAX_AGE_MS is cleared for actions right now, skipping
      // the ~2–3s forced server read. The authoritative re-check below still
      // runs — it will confirm, or swap the profile (→ pending / blocked
      // screen), or on a transient failure leave 'ready' in place for the retry.
      const cachedRecord = getCachedDriverRecord(firebaseUser.uid);
      if (canUnlockFromCache(cachedRecord)) {
        setDriver(cachedRecord.profile);
        setProfileLoaded(true);
        setDefaultsReady(true);
        setProfileStatus('ready');
      }

      // Resolve against the server in the background. Retries never convert a
      // network/App Check failure into a false "new driver" decision.
      const runEnsure = async (retries = 2) => {
        try {
          await resolveAuthoritativeProfile(firebaseUser, { force: retries < 2 });
        } catch (err) {
          console.warn('Authoritative driver bootstrap:', err?.message || err);
          if (retries > 0) {
            retryTimerRef.current = setTimeout(() => runEnsure(retries - 1), 2500);
          }
        }
      };
      runEnsure();
    });
    return () => {
      unsub();
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    };
  }, [resolveAuthoritativeProfile]);

  // Prime the native foreground service's independent Firebase session as soon
  // as the WebView restores a driver login. A temporary mobile-network failure
  // gets bounded retries while signed in; Go Online below also awaits the same
  // bridge and will never claim an Anna is available without it.
  useEffect(() => {
    const uid = user?.uid;
    if (!uid) {
      // On a cold app launch Firebase Auth may need a moment to restore the
      // WebView session. Do not stop an already-running native service during
      // that restoration window; once auth has conclusively settled, this
      // also covers externally-expired/sign-out sessions.
      if (!authLoading) {
        stopBackgroundLocation();
        endNativeAuthSession();
      }
      return undefined;
    }

    let disposed = false;
    let retryTimer = null;
    let attempts = 0;
    const retryDelays = [5000, 15000, 60000];

    const attemptBridge = async () => {
      try {
        await bridgeNativeAuthSession(uid);
        attempts = 0;
      } catch (error) {
        if (disposed) return;
        const delay = retryDelays[Math.min(attempts, retryDelays.length - 1)];
        attempts += 1;
        console.warn('native auth bridge will retry:', error?.code || error?.message || error);
        retryTimer = setTimeout(attemptBridge, delay);
      }
    };
    const retryWhenConnected = () => {
      if (!disposed && !retryTimer) attemptBridge();
    };

    attemptBridge();
    window.addEventListener('online', retryWhenConnected);
    return () => {
      disposed = true;
      if (retryTimer) clearTimeout(retryTimer);
      window.removeEventListener('online', retryWhenConnected);
    };
  }, [user?.uid, authLoading]);

  useEffect(() => {
    if (!user?.uid) {
      setDriver(null);
      setProfileLoaded(false);
      setDefaultsReady(false);
      return undefined;
    }
    return listenToDriverProfile(user.uid, (profile, metadata = {}) => {
      const complete = isProfileComplete(profile);
      if (complete) {
        setDriver(profile);
        setProfileLoaded(true);
        // Only a server snapshot bumps verifiedAt (which gates the optimistic
        // cold-start unlock); a fromCache echo just refreshes the presentation copy.
        setCachedDriverProfile(profile, { verified: Boolean(metadata.authoritative) });
        if (metadata.authoritative) {
          setDefaultsReady(true);
          setProfileStatus('ready');
        }
        return;
      }

      // Cold Firestore listeners commonly emit null/partial cached snapshots
      // before the server reply. Never let those erase a verified returning
      // profile. The authoritative resolver above owns the new-driver decision.
      if (profileStatusRef.current === 'new-driver') {
        setDriver(profile);
        setProfileLoaded(true);
      } else if (metadata.authoritative && profileStatusRef.current === 'ready') {
        // A verified profile was removed or lost its registration fields.
        // Lock protected actions immediately and re-run the authoritative
        // discovery/relink flow before deciding what screen should follow.
        setDefaultsReady(false);
        setProfileStatus('refreshing-profile');
        const firebaseUser = auth.currentUser;
        if (firebaseUser?.uid === user.uid) {
          resolveAuthoritativeProfile(firebaseUser, { force: true })
            .catch((e) => console.warn('Driver profile revalidation:', e?.message || e));
        }
      } else if (!isProfileComplete(driverRef.current) && profile) {
        setDriver(profile);
      }
    });
  }, [user?.uid, resolveAuthoritativeProfile]);

  useEffect(() => {
    if (!user?.uid) {
      setReleaseNotice(null);
      return undefined;
    }
    return listenToPlatformConfig((config) => {
      setReleaseNotice(config?.releaseNotice || null);
    });
  }, [user?.uid]);

  // Self-heal: if driver document exists in Firestore but has no phone number, auto-backfill from auth
  useEffect(() => {
    if (!user?.uid || !profileLoaded || !driver) return;
    if (!driver.phone && user.phoneNumber) {
      const raw = user.phoneNumber;
      const clean10 = String(raw || '').replace(/\D/g, '').slice(-10);
      const norm = clean10.length === 10 ? `+91${clean10}` : raw;
      if (norm) {
        import('../services/firebase').then(({ updateDriverProfile }) => {
          updateDriverProfile(user.uid, { phone: norm }).catch((e) => console.warn('Auto backfill phone failed:', e));
        }).catch(() => {});
      }
    }
  }, [user?.uid, user?.phoneNumber, profileLoaded, driver?.phone]);

  useEffect(() => {
    if (!driverActionsReady || !user?.uid) {
      setRides([]);
      setActiveRideDirect(null);
      return undefined;
    }
    // High priority: active trip check
    const unsubActive = listenToActiveDriverRide(user.uid, setActiveRideDirect);

    // Staggered / deferred: ride history
    let unsubRides = null;
    const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 1000));
    const idleId = idle(() => {
      unsubRides = listenToDriverRides(user.uid, setRides);
    }, { timeout: 2000 });

    return () => {
      try { unsubActive?.(); } catch { /* */ }
      try { unsubRides?.(); } catch { /* */ }
      if (window.cancelIdleCallback) window.cancelIdleCallback(idleId);
      else clearTimeout(idleId);
    };
  }, [driverActionsReady, user?.uid]);

  // Warm the Maps JS SDK immediately on mount — in parallel with Firebase Auth
  // restoring the session, not after — so the first map paint isn't waiting on
  // a cold ~300KB download once profile resolution finishes. Matches the
  // passenger app's own startup pattern (AppContext.js). Safe unconditionally:
  // the script itself isn't billed (only map instances/API calls are), and
  // loadGoogleMaps() is memoized, so every later caller (createMap, etc.)
  // shares this same in-flight/loaded promise instead of double-loading it.
  useEffect(() => {
    import('../services/maps').then((m) => m.loadGoogleMaps()).catch(() => {});
  }, []);

  // Refresh FCM token when already permitted (no prompt). Prompt still shown on Dashboard.
  useEffect(() => {
    if (!driverActionsReady || !user?.uid) return undefined;
    import('../services/notifications')
      .then((m) => m.initDriverMessaging(user.uid, { request: false }))
      .catch(() => {});
    return undefined;
  }, [driverActionsReady, user?.uid]);

  useEffect(() => {
    if (driverActionsReady && user?.uid && isProfileComplete(driver)) {
      clearAwaitingProfile();
    }
  }, [driverActionsReady, user?.uid, driver]);

  // Ride ids the trip screen has already finished/cancelled with. Both ride
  // listeners lag ~200–400ms behind the write, and ActiveTrip navigates home on
  // a fixed timer — without this the Dashboard's "if (activeRide) -> /trip"
  // effect bounces the driver straight back into the just-ended trip.
  const endedRideIdsRef = useRef(new Set());
  const noteRideEnded = useCallback((rideId) => {
    if (!rideId) return;
    endedRideIdsRef.current.add(rideId);
    setActiveRideDirect((cur) => (cur?.id === rideId ? null : cur));
    setTimeout(() => endedRideIdsRef.current.delete(rideId), 20000);
  }, []);

  // A ride the captain reported as stuck (L4) is handed to admin — don't keep
  // bouncing the captain to its trip screen.
  const isActiveStatus = (r) => r
    && ['driver_assigned', 'arrived', 'started'].includes(r.status)
    && r.driverReportedProblem !== true;
  const activeCandidate = isActiveStatus(activeRideDirect)
    ? activeRideDirect
    : (rides.find(isActiveStatus) || null);
  const activeRide = (activeCandidate && endedRideIdsRef.current.has(activeCandidate.id))
    ? null
    : activeCandidate;

  const stopLocationWatch = useCallback(() => {
    if (watchIdRef.current != null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
  }, []);

  const lastKnownPosRef = useRef(null);
  const lastKnownPosAtRef = useRef(0);
  // True when the OS denied location permission / services are off while the
  // driver is online — surfaced as a blocking banner so they don't sit idle
  // thinking they're available while publishing nothing to the dispatch radar.
  const [locationBlocked, setLocationBlocked] = useState(false);
  // Auto-surface the system permission / "turn on GPS" dialog once when the
  // watch first fails, so the Anna doesn't have to spot the banner and tap it.
  // Re-armed the moment a good fix comes back.
  const locationPromptedRef = useRef(false);

  useEffect(() => { driverOnlineRef.current = Boolean(driver?.isOnline); }, [driver?.isOnline]);

  // Raw GPS stays in refs (for publishing + the map marker); React state only
  // advances when the fix moved ≥8m or 10s passed, so consumers don't re-render
  // on every 1s tick.
  const commitMyPos = useCallback((pos) => {
    lastKnownPosRef.current = pos;
    lastKnownPosAtRef.current = Date.now();
    const anchor = myPosStateRef.current;
    const now = Date.now();
    if (anchor) {
      const dlat = (pos.lat - anchor.lat) * 111000;
      const dlng = (pos.lng - anchor.lng) * 111000 * Math.cos((pos.lat * Math.PI) / 180);
      if (Math.hypot(dlat, dlng) < 8 && now - myPosStateAtRef.current < 10000) return;
    }
    myPosStateRef.current = pos;
    myPosStateAtRef.current = now;
    setMyPos(pos);
  }, []);

  // Foreground GPS whenever the driver is approved — powers the home-map dot and
  // the proximity ring maths. Publishes to the dispatch radar whenever online.
  // The native foreground service also publishes (and keeps going once the app
  // is swiped away); both writers share one throttle in publishDriverLocation,
  // so the duplicate is cheap. Having the JS writer always on is deliberate:
  // relying on the native writer alone left drivers invisible when its own
  // Firebase Auth session wasn't ready. Cadence adapts to the moment: slow when
  // offline/parked, moderate when online-waiting; a live trip is handled by the
  // native service + ActiveTrip's own watch, so this one stays gentle then.
  useEffect(() => {
    if (!driverActionsReady || !user?.uid || !driver?.isApproved || driver?.isBlocked) {
      stopLocationWatch();
      setLocationBlocked(false);
      locationPromptedRef.current = false;
      return undefined;
    }

    const onPos = (pos) => {
      if (!pos?.lat || !pos?.lng) return;
      setLocationBlocked(false);
      locationPromptedRef.current = false;
      commitMyPos(pos);
      if (driverOnlineRef.current) {
        publishDriverLocation(user.uid, pos.lat, pos.lng, pos.heading);
      }
    };
    const onWatchError = (err) => {
      // 1 = PERMISSION_DENIED, 2 = POSITION_UNAVAILABLE (services off / no fix)
      if (err && (err.code === 1 || err.code === 2) && driverOnlineRef.current) {
        setLocationBlocked(true);
        if (!locationPromptedRef.current) {
          locationPromptedRef.current = true;
          // Fire-and-forget: on a permanently-denied permission this shows
          // nothing (Android won't re-prompt), and it never touches the watch.
          ensureLocationPermission({ request: true }).catch(() => {});
        }
      }
    };

    const online = Boolean(driver?.isOnline);
    const onTrip = Boolean(activeRide);
    const rate = (!online || onTrip)
      ? { minMs: 6000, minMeters: 20 }
      : { minMs: 2000, minMeters: 6 };
    watchIdRef.current = watchPosition(onPos, onWatchError, rate);

    // Guarantee a fresh fix even if watchPosition stalls (OEM GPS quirks).
    const watchdog = setInterval(() => {
      getCurrentPosition({ maximumAge: 5000, timeout: 15000 }).then(onPos).catch(() => {});
    }, online && !onTrip ? 25000 : 45000);

    // First attempts often fire before the Android permission dialog is answered.
    const retry = () => {
      getCurrentPosition({ maximumAge: 0, timeout: 20000 }).then(onPos).catch(() => {});
    };
    window.addEventListener('mt_location_ready', retry);
    const onVisible = () => { if (document.visibilityState === 'visible') retry(); };
    document.addEventListener('visibilitychange', onVisible);
    const t1 = setTimeout(retry, 600);
    const t2 = setTimeout(retry, 2500);

    return () => {
      stopLocationWatch();
      clearInterval(watchdog);
      window.removeEventListener('mt_location_ready', retry);
      document.removeEventListener('visibilitychange', onVisible);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [driverActionsReady, user?.uid, driver?.isApproved, driver?.isBlocked, driver?.isOnline, Boolean(activeRide), commitMyPos, stopLocationWatch]);

  // Native background foreground-service + stationary heartbeat — online only.
  useEffect(() => {
    if (!driverActionsReady || !user?.uid || !driver?.isOnline) {
      stopBackgroundLocation();
      return undefined;
    }

    const onPos = (pos) => {
      if (!pos?.lat || !pos?.lng) return;
      setLocationBlocked(false);
      commitMyPos(pos);
      // Both this JS path and the native service publish; publishDriverLocation
      // throttles the duplicate. Always writing from JS is the safety net for
      // when the native service's own Firebase Auth session isn't ready.
      if (driverOnlineRef.current) {
        publishDriverLocation(user.uid, pos.lat, pos.lng, pos.heading);
      }
    };
    startBackgroundLocation(onPos);

    // Auto Stand Heartbeat: republish every 45s so parked Annas stay in the
    // radar even when GPS callbacks go quiet (parked = no movement). The native
    // service has its own 45s heartbeat too, for when the app is swiped away.
    const heartbeatTimer = setInterval(() => {
      const p = lastKnownPosRef.current;
      if (p?.lat && p?.lng && driverOnlineRef.current) {
        publishDriverLocation(user.uid, p.lat, p.lng, p.heading);
      }
    }, 45000);

    return () => {
      stopBackgroundLocation();
      clearInterval(heartbeatTimer);
    };
  }, [driverActionsReady, user?.uid, driver?.isOnline, commitMyPos]);

  // Native GPS cadence: full-rate on a trip, battery-saver while parked online.
  useEffect(() => {
    if (!driverActionsReady || !driver?.isOnline) return;
    setLocationMode(activeRide ? 'active' : 'idle');
  }, [driverActionsReady, driver?.isOnline, Boolean(activeRide)]);

  // Web Screen WakeLock — keeps screen on while online or on trip even when running in browser
  useEffect(() => {
    if (!driverActionsReady || (!driver?.isOnline && !activeRide)) return undefined;
    let lock = null;
    const requestLock = async () => {
      try {
        if ('wakeLock' in navigator) {
          lock = await navigator.wakeLock.request('screen');
        }
      } catch { /* unsupported or battery saving mode */ }
    };
    requestLock();
    const onVisChange = () => {
      if (document.visibilityState === 'visible') requestLock();
    };
    document.addEventListener('visibilitychange', onVisChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisChange);
      try { lock?.release?.(); } catch { /* */ }
    };
  }, [driverActionsReady, driver?.isOnline, Boolean(activeRide)]);

  const toggleOnline = useCallback(async () => {
    if (!driverActionsReady || !user?.uid || !driver) return;
    const next = !driver.isOnline;
    // The only temporary lock is while Admin has scheduled this Anna to go
    // offline after an active trip. An Admin taking an idle Anna offline is a
    // normal offline state: the Anna can always choose Go Online again.
    if (next && String(driver.adminDispatchHold?.state || '') === 'after_trip') {
      const error = new Error('New ride requests are paused until the current trip ends');
      error.code = 'admin-dispatch-hold';
      throw error;
    }
    if (next) {
      // Going online must mean actually reachable — a repeat of the "20
      // online, 3 on the map" incident is exactly what these two checks close
      // off. Each one only blocks on a *confirmed* no — an ambiguous or
      // unexpected failure in the check itself always fails open, so a
      // plugin/bridge glitch can never cost an Anna her ability to go online.
      let locationGranted = true;
      try {
        locationGranted = (await ensureLocationPermission({ request: true })) === 'granted';
      } catch (e) {
        // ensureLocationPermission throws exactly this when the Anna declines
        // the native "turn on GPS" prompt it shows itself — a real, confirmed
        // no. Any other throw is a permission-check glitch, not evidence GPS
        // is actually off.
        locationGranted = e?.message !== 'location_disabled';
      }
      if (!locationGranted) {
        const error = new Error('Turn on device location to go online');
        error.code = 'location-required';
        throw error;
      }

      // A cached fix has no timestamp of its own to check — only trust one
      // this recent; anything older (or missing) gets a fresh one-shot fix
      // instead of assuming a possibly stale coordinate is still where she is.
      const MAX_POS_AGE_MS = 30000;
      let pos = lastKnownPosRef.current;
      if (!pos || Date.now() - lastKnownPosAtRef.current > MAX_POS_AGE_MS) {
        try {
          pos = await getCurrentPosition({ timeout: 10000 });
        } catch {
          const error = new Error('Could not get your location yet');
          error.code = 'location-fix-required';
          throw error;
        }
      }

      // supported:false (web, or the check itself failed) always returns
      // ignoring:true from getBatteryOptimizationStatus — never blocks here.
      const battery = await getBatteryOptimizationStatus();
      if (battery.supported && !battery.ignoring) {
        const error = new Error('Allow background location to go online');
        error.code = 'battery-optimization-required';
        throw error;
      }

      // Native tracking writes through the Android Firebase SDK after a
      // WebView is swiped away. Confirm that SDK is signed in as this exact
      // Anna before we publish location or set isOnline=true; otherwise an
      // "online" Anna could disappear and stop receiving offers later.
      try {
        await bridgeNativeAuthSession(user.uid);
      } catch (e) {
        const error = new Error('Could not prepare background tracking — check your connection and try again');
        error.code = 'native-auth-required';
        throw error;
      }

      // Clear any stale "go offline" command left from an earlier session
      // first — the driverLocations RTDB rule rejects every write while it's
      // set, and setDriverOnline below only clears it *after* this publish.
      // Doing it in that order deadlocks: the flag only clears by going
      // online, and going online needs the flag already clear.
      //
      // Confirm this exact fix actually reaches the dispatch radar before
      // isOnline flips — otherwise "online" and "visible on the map" can
      // still drift apart during the gap before the next natural GPS tick.
      // force bypasses the publish throttle so this write is never silently
      // skipped as a near-duplicate of a recent one. A failure here must
      // block — if the write didn't land, she genuinely isn't visible yet.
      try {
        await clearRemoteOfflineCommand(user.uid);
        await publishDriverLocation(user.uid, pos.lat, pos.lng, pos.heading, { force: true });
      } catch (e) {
        const error = new Error('Could not publish your location — check your connection and try again');
        error.code = 'location-publish-failed';
        throw error;
      }
    }
    await setDriverOnline(user.uid, next);
    if (!next) {
      // The RTDB command above is the server-side safety backstop. Stop the
      // service directly as well so a sticky restart cannot retain the prior
      // Anna's tracking session while this WebView remains alive.
      await stopBackgroundLocation();
      await clearDriverLocation(user.uid);
      await endNativeAuthSession();
    }
  }, [driverActionsReady, user?.uid, driver]);

  // Registration is reachable only from a conclusive server result. Temporary
  // auth, cache, App Check, Firestore, and callable failures remain bootstrap
  // states and can never route a returning captain into registration.
  const needsProfile = Boolean(user && profileStatus === 'new-driver');

  const refreshDriverProfile = useCallback(async (uid, { force = false } = {}) => {
    const firebaseUser = auth.currentUser;
    if (!uid || !firebaseUser || firebaseUser.uid !== uid) return null;
    return resolveAuthoritativeProfile(firebaseUser, { force });
  }, [resolveAuthoritativeProfile]);

  return (
    <DriverContext.Provider value={{
      user,
      driver,
      authLoading,
      profileReady,
      profileLoaded,
      defaultsReady,
      profileStatus,
      driverActionsReady,
      needsProfile,
      rides,
      activeRide,
      myPos,
      noteRideEnded,
      locationBlocked,
      toggleOnline,
      refreshDriverProfile,
      releaseNotice,
      rideOfferOpen,
      setRideOfferOpen,
    }}>
      {children}
    </DriverContext.Provider>
  );
};

export const useDriver = () => {
  const ctx = useContext(DriverContext);
  if (!ctx) throw new Error('useDriver must be used within DriverProvider');
  return ctx;
};
